import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import { useLanguage } from "../i18n/LanguageContext";
import { Briefcase, Users, ShieldCheck, TrendingUp, Mountain, Network, Quote } from "lucide-react";

const ICONS = {
  workshops: Briefcase,
  team: Users,
  stress: ShieldCheck,
  verkauf: TrendingUp,
  retreats: Mountain,
  aufstellung: Network,
};

const heroImg = "/assets/duo_portrait.png";

const BusinessPage = () => {
  const { t } = useLanguage();
  const b = t.business;

  return (
    <div data-testid="business-page" className="theme-business min-h-screen bg-[#F7F6F2] text-[#1F2A24]">
      <Header variant="business" />

      {/* HERO */}
      <section className="px-6 md:px-12 lg:px-20 pt-10 md:pt-16 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-7 fade-up">
          <span className="eyebrow text-[#5C6660]">{b.hero.eyebrow}</span>
          <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-[#1F2A24]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            {b.hero.title}
          </h1>
          <p className="mt-7 text-base md:text-lg text-[#3C4842] max-w-2xl leading-relaxed">
            {b.hero.sub}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a data-testid="business-hero-cta-primary" href="#contact" className="inline-flex items-center px-6 py-3 bg-[#1F2A24] text-white text-sm uppercase tracking-[0.22em] hover:bg-[#2A3A30] transition rounded-full">{b.hero.ctaPrimary}</a>
            <a data-testid="business-hero-cta-secondary" href="#offers" className="inline-flex items-center px-6 py-3 border border-[#1F2A24] text-[#1F2A24] text-sm uppercase tracking-[0.22em] hover:bg-[#1F2A24] hover:text-white transition rounded-full">{b.hero.ctaSecondary}</a>
          </div>
        </div>
        <div className="lg:col-span-5 fade-up delay-2">
          <div className="relative aspect-[4/5] w-full rounded-3xl overflow-hidden bg-[#E8E4DC]">
            <img
              src={heroImg}
              alt="Team-Coaching & Business-Workshops"
              className="w-full h-full object-cover"
              style={{ filter: "saturate(0.85) brightness(0.96) contrast(1.02)" }}
            />
            <div className="absolute inset-0 ring-1 ring-[#C8C2B6] rounded-3xl pointer-events-none" />
          </div>
        </div>
      </section>

      {/* OFFERS */}
      <section id="offers" className="px-6 md:px-12 lg:px-20 py-20 border-t border-[#C8C2B6]">
        <div className="max-w-3xl">
          <span className="eyebrow text-[#5C6660]">{b.offers.eyebrow}</span>
          <h2 className="mt-5 text-3xl md:text-4xl text-[#1F2A24]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{b.offers.title}</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {b.offers.items.map((item) => {
            const Icon = ICONS[item.id] || Briefcase;
            return (
              <div
                key={item.id}
                data-testid={`business-offer-${item.id}`}
                className="bg-white border border-[#D8D2C5] rounded-2xl p-8 hover:bg-[#F0EDE5] transition group"
              >
                <Icon size={28} className="text-[#5A7060] mb-6 transition-transform group-hover:-translate-y-1" />
                <h3 className="text-xl md:text-2xl text-[#1F2A24] leading-snug" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{item.title}</h3>
                <p className="mt-4 text-[#3C4842] leading-relaxed">{item.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* DUO — Both coaches */}
      <section className="px-6 md:px-12 lg:px-20 py-20 border-t border-[#C8C2B6]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow text-[#5C6660]">{b.duo.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl text-[#1F2A24]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{b.duo.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#3C4842] leading-relaxed">{b.duo.body}</p>
            <div className="mt-8 flex flex-wrap gap-3 text-xs uppercase tracking-[0.2em] text-[#5A7060]">
              <Link to="/men" className="px-4 py-2 border border-[#5A7060] hover:bg-[#5A7060] hover:text-white transition rounded-full" data-testid="business-link-enrico">Mehr über Enrico →</Link>
              <Link to="/women" className="px-4 py-2 border border-[#5A7060] hover:bg-[#5A7060] hover:text-white transition rounded-full" data-testid="business-link-colleen">Mehr über Colleen →</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <blockquote className="flex items-start gap-4 text-[#1F2A24]">
              <Quote className="text-[#5A7060] mt-1 shrink-0" />
              <span className="text-xl md:text-2xl italic leading-snug" style={{ fontFamily: "Cormorant Garamond, serif" }}>
                Performance entsteht da, wo Klarheit und Tiefe sich begegnen.
              </span>
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA / CONTACT */}
      <section id="contact" className="px-6 md:px-12 lg:px-20 py-20 border-t border-[#C8C2B6]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="eyebrow text-[#5C6660]">{b.cta.eyebrow}</span>
            <h2 className="mt-5 text-3xl md:text-4xl text-[#1F2A24]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{b.cta.title}</h2>
            <p className="mt-6 text-base md:text-lg text-[#3C4842] leading-relaxed">{b.cta.sub}</p>
            {b.cta.investmentNote && (
              <p className="mt-4 text-sm text-[#5A7060] italic">{b.cta.investmentNote}</p>
            )}
            <p className="mt-4 text-xs text-[#5C6660]">{b.legalNote}</p>
          </div>
          <div className="lg:col-span-7">
            <h3 className="text-lg md:text-xl text-[#1F2A24] mb-6" style={{ fontFamily: "Cormorant Garamond, serif" }}>{b.cta.formTitle}</h3>
            <ContactForm pathKey="business" />
          </div>
        </div>
      </section>

      <Footer variant="business" />
    </div>
  );
};

export default BusinessPage;
